import { FiAlertTriangle, FiCheckCircle, FiXCircle } from "react-icons/fi"
import { useLanguage } from "../../contexts/LanguageContext"
import { useDarkMode } from "../../hooks/useDarkMode"

export default function UsageProgress({ label, current, quota, unit = "", showWarning = true, icon = null }) {
  const { t, isRTL } = useLanguage()
  const { darkMode }  = useDarkMode()

  // -1 is the canonical "unlimited" sentinel from plansConfig
  // (Enterprise quotas). 999999 is kept for backward compatibility
  // with any tenant docs still carrying the old value.
  const isUnlimited = quota === -1 || quota === 999999;
  // quota=0 means the plan doesn't include this feature at all (e.g.
  // Free has pdf_monthly=0). Don't render that as "exceeded" — there's
  // no path forward except upgrade. Treat it the same as the unlimited
  // case for the percentage math (no progress bar) but keep the warning
  // copy explicit.
  const isFeatureLocked = quota === 0;
  const percentage  = isFeatureLocked ? 0 : Math.min((current / quota) * 100, 100);
  const isExceeded  = !isUnlimited && !isFeatureLocked && current >= quota;
  const isWarning   = !isExceeded && !isUnlimited && !isFeatureLocked && percentage >= 80;
  const isOk        = !isExceeded && !isWarning;

  const color = isExceeded
    ? { bar: "bg-red-500",    text: "text-red-400",    icon: FiXCircle      }
    : isWarning
    ? { bar: "bg-yellow-500", text: "text-yellow-400", icon: FiAlertTriangle }
    : { bar: "bg-green-500",  text: "text-green-400",  icon: FiCheckCircle  }

  const StatusIcon = color.icon

  return (
    <div className={`border rounded-xl p-4 ${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"}`}>

      {/* Header */}
      <div className={`flex items-center justify-between mb-2`}>
        <div className={`flex items-center gap-2`}>
          {icon && <span className={darkMode ? "text-gray-400" : "text-gray-500"}>{icon}</span>}
          <span className={`font-medium ${darkMode ? "text-gray-300" : "text-gray-700"}`}>{label}</span>
        </div>
        {showWarning && !isOk && (
          <div className={`${color.text} flex items-center gap-1 text-sm`}>
            <StatusIcon className="text-xs" />
            <span>{isExceeded ? t("quota_exceeded") : t("quota_warning")}</span>
          </div>
        )}
      </div>

      {/* Numbers */}
      <div className={`flex justify-between items-baseline mb-2`}>
        <span className={`text-2xl font-bold ${color.text}`}>{isUnlimited ? "∞" : current}</span>
        <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
          {isUnlimited
            ? t("unlimited")
            // For locked features (quota=0) we still show "0" rather
            // than the raw "0" denominator — keeps the visual quiet.
            : `${t("of")} ${isFeatureLocked ? 0 : quota} ${unit}`}
        </span>
      </div>

      {/* Progress bar */}
      {!isUnlimited && (
        <div className={`w-full rounded-full h-2 overflow-hidden ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}>
          <div
            className={`h-full transition-all duration-500 ${color.bar}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}

      {/* Messages */}
      {showWarning && isExceeded && (
        <p className="text-red-400 text-xs mt-2">{t("quota_exceeded_msg")}</p>
      )}
      {showWarning && isWarning && (
        <p className="text-yellow-400 text-xs mt-2">
          {Math.round(100 - percentage)}% {t("quota_remaining_msg")}
        </p>
      )}
    </div>
  )
}